import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  IconButton,
  Popover,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import {
  convertFromRaw,
  convertToRaw,
  EditorState,
  ContentState,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { MoreHorizontalIcon } from 'lucide-react';
import moment from 'moment/moment';
import * as yup from 'yup';

import { DataGrid } from '@/components';
import { DataGridProps } from '@/components/DataGrid/types';
import { FormTextEditorField } from '@/components/FormElements';
import {
  SidePaneModal,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/TailwindComponents';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { DATE_FORMAT, TIME_FORMAT } from '@/config';
import { formatPersonName } from '@/utils';

const editorStateRequired = yup
  .mixed<EditorState>()
  .test(
    'required',
    'This field is required.',
    (value) =>
      value instanceof EditorState && value.getCurrentContent().hasText(),
  );

const getFormSchema = (showTypeSelect: boolean) => {
  if (showTypeSelect) {
    return yup.object().shape({
      typeId: yup.string().required('This field is required.').trim(),
      comment: editorStateRequired,
    });
  }
  return yup.object().shape({
    typeId: yup.string().default(''),
    comment: editorStateRequired,
  });
};

type FormData = {
  typeId: string;
  comment: EditorState;
};

export const parseNoteDescription = (
  value: string | undefined,
): EditorState => {
  if (!value) return EditorState.createEmpty();
  try {
    const parsed = JSON.parse(value);
    const raw = parsed?.text || parsed;
    if (raw?.blocks) {
      return EditorState.createWithContent(convertFromRaw(raw));
    }
  } catch {
    // Legacy plain text
    if (value.trim()) {
      return EditorState.createWithContent(ContentState.createFromText(value));
    }
  }
  return EditorState.createEmpty();
};

export const serializeEditorState = (editorState: EditorState): string => {
  const content = editorState.getCurrentContent();
  if (!content.hasText()) return '';
  return JSON.stringify({ text: convertToRaw(content) });
};

const editorStateToHtml = (value: string | undefined): string => {
  if (!value) return '';
  try {
    const parsed = JSON.parse(value);
    const raw = parsed?.text || parsed;
    if (raw?.blocks) {
      return draftToHtml(raw);
    }
  } catch {
    // Legacy plain text
    return value;
  }
  return value;
};

export const getPlainTextFromDescription = (
  value: string | undefined,
): string => {
  if (!value) return '';
  try {
    const parsed = JSON.parse(value);
    const raw = parsed?.text || parsed;
    if (raw?.blocks) {
      return convertFromRaw(raw).getPlainText();
    }
  } catch {
    // Legacy plain text
  }
  return value;
};

interface NoteSideModalProps<T = any> {
  open: boolean;
  onClose: () => void;
  mode: 'view' | 'edit' | 'add';
  noteData: any;
  onSave: (data: any) => void;
  showTypeSelect?: boolean;
  noteTypes?: T[];
  fetchNoteDetails?: (noteId: string) => Promise<any>;
  addressRelatedTo?: string;
}

const NoteSideModal = <T extends { _id?: string; name?: string }>({
  open,
  onClose,
  mode,
  noteData,
  onSave,
  showTypeSelect = false,
  noteTypes = [],
  fetchNoteDetails,
  addressRelatedTo,
}: NoteSideModalProps<T>) => {
  const [fullNoteData, setFullNoteData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const formMethods = useForm<FormData>({
    resolver: yupResolver(getFormSchema(showTypeSelect)) as any,
    defaultValues: {
      typeId: (noteData?.type?._id as string) || '',
      comment: parseNoteDescription(noteData?.comment || noteData?.description),
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = formMethods;

  const formData = watch();

  useEffect(() => {
    const loadNoteDetails = async () => {
      if (noteData?._id && fetchNoteDetails) {
        setIsLoading(true);
        try {
          const details = await fetchNoteDetails(noteData._id);
          setFullNoteData(details);
          reset({
            typeId: (details?.type?._id as string) || '',
            comment: parseNoteDescription(
              details?.comment || details?.description,
            ),
          });
        } catch (error) {
          /* empty */
        } finally {
          setIsLoading(false);
        }
      } else {
        reset({
          typeId: (noteData?.type?._id as string) || '',
          comment: parseNoteDescription(
            noteData?.comment || noteData?.description,
          ),
        });
      }
    };

    if (open) loadNoteDetails();
  }, [open, noteData, fetchNoteDetails, reset]);

  const isViewMode = mode === 'view';
  const title =
    mode === 'view' ? 'View note' : mode === 'edit' ? 'Edit note' : 'Add note';

  const selectedType =
    noteTypes.find((t) => String(t._id) === String(formData.typeId)) ||
    fullNoteData?.type ||
    null;

  const viewHtml = useMemo(() => {
    const raw = fullNoteData?.comment || fullNoteData?.description;
    return editorStateToHtml(raw);
  }, [fullNoteData]);

  const onSubmit = (data: FormData) => {
    const typeName = selectedType?.name || '';
    onSave({ comment: serializeEditorState(data.comment), type: typeName });
    onClose();
  };

  const handleSaveClick = () => {
    handleSubmit(onSubmit)();
  };

  const noteInformation = () => {
    return (
      <div>
        <div className="border-gray-200 dark:border-[#192932] border-t !h-[1px] pb-4" />
        <div className="flex flex-col gap-3">
          <div>
            {fullNoteData?.createdBy ? (
              <>
                <label className="block mb-1 text-xs text-gray-400 dark:text-gray-500">
                  {!showTypeSelect ? 'Created by' : 'Added by'}
                </label>
                <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap dark:text-gray-100">
                  {formatPersonName(fullNoteData?.createdBy)}
                </div>
              </>
            ) : null}
          </div>
          <div>
            {fullNoteData?.createdAt ? (
              <>
                <label className="block mb-1 text-xs text-gray-400 dark:text-gray-500">
                  {!showTypeSelect ? 'Created on' : 'Date and time'}
                </label>
                <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap dark:text-gray-100">
                  {moment(fullNoteData.createdAt).format(DATE_FORMAT)}{' '}
                  {showTypeSelect
                    ? moment(fullNoteData.createdAt).format(TIME_FORMAT)
                    : null}
                </div>
              </>
            ) : null}
          </div>
          <>
            {!showTypeSelect &&
            (fullNoteData?.updatedBy || fullNoteData?.createdBy) ? (
              <div>
                <label className="block text-xs text-gray-400 dark:text-gray-500">
                  Last updated by
                </label>
                <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap dark:text-gray-100">
                  {formatPersonName(fullNoteData?.updatedBy) ||
                    formatPersonName(fullNoteData?.createdBy)}
                </div>
              </div>
            ) : null}
          </>
          <>
            {!showTypeSelect &&
            ((fullNoteData?.updatedAt && fullNoteData?.updatedBy) ||
              (fullNoteData?.createdBy && fullNoteData?.createdAt)) ? (
              <div>
                <label className="block text-xs text-gray-400 dark:text-gray-500">
                  Last updated on
                </label>
                <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap dark:text-gray-100">
                  {fullNoteData?.updatedAt
                    ? moment(fullNoteData.updatedAt).format(DATE_FORMAT)
                    : moment(fullNoteData.createdAt).format(DATE_FORMAT)}
                </div>
              </div>
            ) : null}
          </>
          <>
            {!showTypeSelect ? (
              <div>
                <label className="block text-xs text-gray-400 dark:text-gray-500">
                  Reference number
                </label>
                <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap dark:text-gray-100">
                  {fullNoteData?.cfs?.number ||
                    fullNoteData?.kase?.number ||
                    '/'}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs text-gray-400 dark:text-gray-500">
                  Related to
                </label>
                <div className="text-sm font-semibold text-gray-900 whitespace-pre-wrap dark:text-gray-100">
                  {addressRelatedTo}
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    );
  };

  return (
    <SidePaneModal
      title={title}
      side="right"
      open={open}
      onOpenChange={onClose}
    >
      <SidePaneModal.Content>
        <div
          ref={contentRef}
          className="p-1 space-y-4 text-gray-900 dark:text-gray-100"
        >
          {isLoading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : (
            <>
              {showTypeSelect && (
                <div>
                  <label
                    className={cn(
                      'block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1',
                      {
                        'text-gray-400 dark:text-gray-500 text-xs font-normal':
                          isViewMode,
                      },
                    )}
                  >
                    Type{' '}
                    {!isViewMode ? (
                      <span className="text-red-500">*</span>
                    ) : null}
                  </label>
                  {isViewMode ? (
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {selectedType?.name || 'N/A'}
                    </div>
                  ) : (
                    <>
                      <Select
                        value={formData.typeId}
                        onValueChange={(value) =>
                          setValue('typeId', value, { shouldValidate: true })
                        }
                      >
                        <SelectTrigger className="w-full" autoFocus={false}>
                          <SelectValue placeholder="Select a type" />
                        </SelectTrigger>
                        <SelectContent container={contentRef.current}>
                          {noteTypes.map((t) => (
                            <SelectItem
                              key={String(t._id ?? t.name)}
                              value={String(t._id ?? '')}
                            >
                              {t.name || 'Unknown'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.typeId && (
                        <span className="block mt-1 text-xs text-red-500">
                          {errors.typeId.message}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}

              <div
                className={cn({
                  'bg-gray-100 dark:bg-[#192932] p-4 rounded-l': isViewMode,
                })}
              >
                <label
                  className={cn(
                    'block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1',
                    {
                      'text-gray-400 dark:text-gray-500 text-xs font-normal':
                        isViewMode,
                    },
                  )}
                >
                  {showTypeSelect ? 'Comment' : 'Note description'}{' '}
                  {!isViewMode ? <span className="text-red-500">*</span> : null}
                </label>
                {isViewMode ? (
                  <div
                    className="text-sm text-gray-900 dark:text-gray-100 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_p:empty]:h-4"
                    dangerouslySetInnerHTML={{
                      __html: viewHtml || 'N/A',
                    }}
                  />
                ) : (
                  <FormProvider {...formMethods}>
                    <FormTextEditorField
                      name="comment"
                      rules={{ required: true }}
                    />
                    {errors.comment && (
                      <span className="block text-xs text-red-500">
                        {errors.comment.message}
                      </span>
                    )}
                  </FormProvider>
                )}
              </div>
              {!isViewMode && noteData ? noteInformation() : null}
              {isViewMode ? noteInformation() : null}
            </>
          )}
        </div>
      </SidePaneModal.Content>
      <SidePaneModal.Footer>
        <Button variant="outline" onClick={onClose} type="button">
          {isViewMode ? 'Close' : 'Cancel'}
        </Button>
        {!isViewMode && (
          <Button onClick={handleSaveClick} type="button">
            Save
          </Button>
        )}
      </SidePaneModal.Footer>
    </SidePaneModal>
  );
};

interface NotesDataGridWrapperProps<T = any>
  extends Omit<DataGridProps, 'columns'> {
  onSaveNote?: (noteData: any) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
  readOnly?: boolean;
  additionalColumns?: GridColDef[];
  showTypeSelect?: boolean;
  noteTypes?: T[];
  fetchNoteDetails?: (noteId: string) => Promise<any>;
  addressRelatedTo?: string;
}

export default function NotesDataGridWrapper<
  T extends { _id?: string; name?: string },
>({
  fetchData,
  columnSettings,
  setColumnSettings,
  onSaveNote,
  onDeleteNote,
  readOnly = false,
  additionalColumns = [],
  showTypeSelect = false,
  noteTypes = [],
  fetchNoteDetails,
  addressRelatedTo,
  ...props
}: NotesDataGridWrapperProps<T>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'add'>('view');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, note: any) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setSelectedNote(note);
    },
    [],
  );

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleViewNote = useCallback(() => {
    setModalMode('view');
    setModalOpen(true);
    handleCloseMenu();
  }, [handleCloseMenu]);

  const handleEditNote = useCallback(() => {
    if (readOnly) return;
    setModalMode('edit');
    setModalOpen(true);
    handleCloseMenu();
  }, [handleCloseMenu, readOnly]);

  const handleAddNote = useCallback(() => {
    if (readOnly) return;
    setSelectedNote(null);
    setModalMode('add');
    setModalOpen(true);
  }, [readOnly]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedNote(null);
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (readOnly) return;
    if (selectedNote?._id) {
      setNoteToDelete(selectedNote._id);
      setConfirmDeleteOpen(true);
      handleCloseMenu();
    }
  }, [selectedNote, handleCloseMenu, readOnly]);

  const handleConfirmDelete = useCallback(async () => {
    if (noteToDelete && onDeleteNote) {
      await onDeleteNote(noteToDelete);
      setConfirmDeleteOpen(false);
      setNoteToDelete(null);
    }
  }, [noteToDelete, onDeleteNote]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
    setNoteToDelete(null);
  }, []);

  const handleSaveNote = useCallback(
    async (noteData: any) => {
      if (onSaveNote) {
        await onSaveNote({
          ...noteData,
          _id: modalMode === 'edit' ? selectedNote?._id : undefined,
        });
      }
    },
    [onSaveNote, modalMode, selectedNote],
  );

  const actionsColumn: GridColDef = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams) =>
      readOnly ? null : (
        <IconButton
          size="small"
          onClick={(e) => handleOpenMenu(e, params.row)}
          sx={{ color: 'text.secondary' }}
        >
          <MoreHorizontalIcon strokeWidth={1} size={20} />
        </IconButton>
      ),
  };

  const allColumns = [...additionalColumns, actionsColumn];

  return (
    <>
      <DataGrid
        columns={allColumns}
        fetchData={fetchData}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        hideQuickFilter
        hideFilter
        cta={
          readOnly ? undefined : (
            <Button onClick={handleAddNote}>Add note</Button>
          )
        }
        onRowClick={(params) => {
          setSelectedNote(params.row);
          handleViewNote();
        }}
        {...props}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{ p: 1, borderRadius: 4, minWidth: 160 }}
          className="bg-white dark:bg-[#17252D]"
        >
          <MenuItem onClick={handleViewNote}>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              View
            </div>
          </MenuItem>
          {!readOnly && (
            <MenuItem onClick={handleEditNote}>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                Edit
              </div>
            </MenuItem>
          )}
          {!readOnly && (
            <MenuItem onClick={handleDeleteClick}>
              <div className="font-semibold text-red-500">Delete</div>
            </MenuItem>
          )}
        </Box>
      </Popover>

      <NoteSideModal
        open={modalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        noteData={selectedNote}
        onSave={handleSaveNote}
        showTypeSelect={showTypeSelect}
        noteTypes={noteTypes}
        fetchNoteDetails={fetchNoteDetails}
        addressRelatedTo={addressRelatedTo}
      />

      <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
        <DialogTitle id="confirm-delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent aria-labelledby="confirm-delete-dialog-title">
          <Typography>
            Are you sure you want to delete this note? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
